import React from 'react'
import EditIcon from '@mui/icons-material/Edit'
// .mjs so plain node can import it in its test without a build step, and
// webpack resolves it natively.
import { editSummary } from './EditBadge.logic.mjs'

// The mark on a photo's tile saying it carries Lightroom edits.
//
// Without it the only way to know an edit existed was to open the photo and
// look for a sidecar, which meant a grid of a thousand raws gave no clue which
// of them had been graded.
//
// A camera reading is NOT an edit, and gets no mark at all here. Lightroom
// writes an .xmp for a photograph nobody has opened — camera profile, EXIF, a
// crop over the whole frame — and three fifths of the .xmp files in this
// library are exactly that. Badging them credited people with work they never
// did; badging them differently just put a mark on almost every tile, which
// told you nothing. They live in the photo, behind the aperture button.
//
// Everything here comes from the library query, which already carries each
// sidecar's version count, editors and classification — the tile must not fire
// a request per photo to render a badge.

export default function EditBadge({ sidecars, styles }) {
  const summary = editSummary(sidecars)
  if (!summary) return null

  // Nothing anybody did. The camera's own settings are attached, but saying so
  // on every tile is noise: most raws in the library carry one, so the mark
  // would appear almost everywhere and distinguish almost nothing. They are
  // still there behind the aperture button once a photo is opened.
  if (!summary.editFiles) return null

  // The title carries the detail the chip has no room for, so hovering answers
  // "who, and how many versions" without opening the photo.
  const title = [
    `${summary.editFiles} edit file${summary.editFiles === 1 ? '' : 's'}`,
    summary.versions > 1 ? `${summary.versions} saved versions` : null,
    summary.named.length ? `edited by ${summary.named.join(', ')}` : null,
    summary.anyAnonymous ? 'includes an unattributed save' : null,
    summary.sources.length ? `from ${summary.sources.join(', ')}` : null,
    summary.hasMasks ? 'includes masks (.acr)' : null,
    summary.cameraFiles ? `plus ${summary.cameraFiles} camera settings file${summary.cameraFiles === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <span className={styles.editBadge} title={title}>
      <EditIcon style={{ fontSize: '0.75rem' }} />
      {summary.label}
      {summary.versions > 1 && <em className={styles.editBadgeVersions}>×{summary.versions}</em>}
    </span>
  )
}
